import { useEffect, useRef, useState } from 'react';
import { Modal } from '../../../shared/components/Modal';
import Button from '../../../shared/components/Button';
import { useGuardrails, useSetGuardrails } from '../hooks/useCustomOrders';

const MB = 1024 * 1024;
const toMb = (bytes: number | undefined) => (bytes ? Math.round((bytes / MB) * 10) / 10 : 0);
const toBytes = (mb: number) => Math.round(mb * MB);

const ROWS: { key: 'imageMaxBytes' | 'videoMaxBytes' | 'audioMaxBytes'; label: string }[] = [
  { key: 'imageMaxBytes', label: 'Image' },
  { key: 'videoMaxBytes', label: 'Video' },
  { key: 'audioMaxBytes', label: 'Audio' },
];

interface Props {
  tenantId: string;
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Per-asset-type upload size limits (PE-1897). Edits media-service's
 * MerchantMediaPolicy via the BFF — the same caps enforced at upload time and
 * read by the storefront uploader. Values are shown in MB, persisted in bytes.
 */
export const GuardrailSettingsModal = ({ tenantId, isOpen, onClose }: Props) => {
  const { data, isLoading, isFetching } = useGuardrails(isOpen ? tenantId : null);
  const setGuardrails = useSetGuardrails();
  const [mb, setMb] = useState({ image: 0, video: 0, audio: 0 });

  // Tenant-drift guard: pin the modal to the merchant it opened for. If the admin
  // switches the selected merchant while this is open, close instead of letting a
  // save land on a different tenant than the one whose limits are displayed — the
  // write is keyed purely by the x-tenant-id header (read live at request time).
  const openedForTenant = useRef<string | null>(null);
  useEffect(() => {
    if (!isOpen) {
      openedForTenant.current = null;
      return;
    }
    if (openedForTenant.current === null) openedForTenant.current = tenantId;
    else if (openedForTenant.current !== tenantId) onClose();
  }, [isOpen, tenantId, onClose]);

  useEffect(() => {
    if (data) {
      setMb({
        image: toMb(data.imageMaxBytes),
        video: toMb(data.videoMaxBytes),
        audio: toMb(data.audioMaxBytes),
      });
    }
  }, [data]);

  const valid = mb.image > 0 && mb.video > 0 && mb.audio > 0 && !isFetching;

  const handleSave = async () => {
    if (!valid) return;
    await setGuardrails.mutateAsync({
      imageMaxBytes: toBytes(mb.image),
      videoMaxBytes: toBytes(mb.video),
      audioMaxBytes: toBytes(mb.audio),
    });
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="sm">
      <div className="p-8">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-xl font-semibold text-foreground">Upload limits</h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-muted-foreground text-2xl leading-none">
            &times;
          </button>
        </div>
        <p className="text-[13px] text-muted-foreground mb-6">
          Maximum file size customers can upload per asset type, in megabytes. Enforced at upload.
        </p>

        {isLoading ? (
          <div className="py-10 text-center text-muted-foreground text-[14px]">Loading limits…</div>
        ) : (
          <div className="flex flex-col gap-4">
            {ROWS.map((row) => {
              const field = row.key === 'imageMaxBytes' ? 'image' : row.key === 'videoMaxBytes' ? 'video' : 'audio';
              return (
                <div key={row.key} className="flex items-center justify-between gap-4">
                  <label className="text-[14px] text-foreground" htmlFor={row.key}>
                    {row.label}
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      id={row.key}
                      type="number"
                      min={1}
                      value={mb[field] || ''}
                      onChange={(e) => setMb((p) => ({ ...p, [field]: Number(e.target.value) }))}
                      className="w-28 rounded-[8px] border border-[#D9D9D9] px-3 py-2 text-[14px] text-right outline-none focus:border-[#7C5CFC]"
                    />
                    <span className="text-[13px] text-muted-foreground">MB</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="flex justify-end gap-3 mt-8">
          <Button variant="outline" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button size="sm" onClick={handleSave} disabled={!valid || setGuardrails.isPending} loading={setGuardrails.isPending}>
            Save limits
          </Button>
        </div>
      </div>
    </Modal>
  );
};
